import { useState, useEffect } from 'react'
import { useNotification, useUserSession } from 'hooks'
import AffiliateService from 'modules/affiliates/services/affiliate-service'
import ProfileCrop from 'modules/affiliates/components/ProfileCrop'
import BannerCrop from 'modules/affiliates/components/BannerCrop'
import { Container, Button } from 'components/ui'
import s from 'pages/cadastro-afiliados/index.module.css'

interface AffiliateChangeDataProps {}

const AffiliateChangeData: React.FC<AffiliateChangeDataProps> = () => {
  const { user } = useUserSession()
  const { notify } = useNotification()
  const [loading, setLoading] = useState(false)
  const [affiliateData, setAffiliateData] = useState<any>({})
  const [profileBase64, setProfileBase64] = useState('')
  const [bannerBase64, setBannerBase64] = useState('')
  const [slugError, setSlugError] = useState('')
  const [showComoConheceuOutros, setShowComoConheceuOutros] = useState(false)
  const [showAtividadeOutros, setShowAtividadeOutros] = useState(false)

  useEffect(() => {
    async function fetchAffiliate() {
      setLoading(true)
      try {
        const service = new AffiliateService()
        const response = await service.setUrl(`/afiliados/${user.afiliadoId}`).get()
        const data = response.data
        setAffiliateData({
          ...data,
          imagem_perfil: '',
          imagem_banner: '',
        })
        setProfileBase64('')
        setBannerBase64('')
        setShowComoConheceuOutros(data.como_conheceu === 'outros')
        setShowAtividadeOutros(data.atividade_principal === 'outros')
      } catch (e) {
        notify({ title: 'Erro', description: 'Erro ao carregar dados do afiliado', type: 'danger' })
      } finally {
        setLoading(false)
      }
    }
    if (user?.afiliadoId) fetchAffiliate()
  }, [user])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setAffiliateData((prev: any) => ({ ...prev, [name]: value }))
    if (name === 'slug') {
      const regex = /^[a-z0-9-]+$/
      setSlugError(regex.test(value) ? '' : 'Use apenas letras minúsculas, números e hífens.')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const data = {
        ...affiliateData,
        imagem_perfil_file: profileBase64 || undefined,
        imagem_banner_file: bannerBase64 || undefined,
      }
      delete data.contas_bancarias
      delete data.pis
      const service = new AffiliateService()
      await service.setUrl(`/afiliados/${user.afiliadoId}`).put(data)
      notify({ title: 'Sucesso', description: 'Dados atualizados com sucesso!', type: 'success' })
    } catch (e) {
      notify({ title: 'Erro', description: 'Erro ao atualizar dados do afiliado', type: 'danger' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={s.cadastro_afiliados}>
      <Container className={s.root}>
        <h1 className={s.title}>Editar dados do afiliado</h1>
        <form onSubmit={handleSubmit} className={s.form}>

          <div className={s.formSection}>
            <h2 className={s.sectionTitle}>Redes Sociais</h2>
            <div className={s.formGroup}>
              <label htmlFor="instagram">Instagram</label>
              <input type="text" id="instagram" name="instagram" placeholder="https://instagram.com/seu-usuario" value={affiliateData.instagram || ''} onChange={handleChange} />
            </div>
            <div className={s.formGroup}>
              <label htmlFor="tiktok">TikTok</label>
              <input type="text" id="tiktok" name="tiktok" placeholder="https://tiktok.com/@seu-usuario" value={affiliateData.tiktok || ''} onChange={handleChange} />
            </div>
            <div className={s.formGroup}>
              <label htmlFor="youtube">YouTube</label>
              <input type="text" id="youtube" name="youtube" placeholder="https://youtube.com/c/seu-canal" value={affiliateData.youtube || ''} onChange={handleChange} />
            </div>
          </div>

          <div className={s.formSection}>
            <h2 className={s.sectionTitle}>Dados do Afiliado</h2>
            <div className={s.formGroup}>
              <label htmlFor="slug">Personalize sua página na Fazendinha *</label>
              <input
                type="text"
                id="slug"
                name="slug"
                required
                value={affiliateData.slug || ''}
                onChange={handleChange}
                autoComplete="off"
                placeholder="ex: seunomeafiliado"
              />
              <small>Este será o endereço da sua página de afiliado. Exemplo: fazendinha.me/afiliado/<b>seunomeafiliado</b></small>
              {slugError && <div style={{color: 'red', fontSize: 13, marginTop: 4}}>{slugError}</div>}
            </div>
            <div className={s.formGroup}>
              <label htmlFor="titulo">Título da Página de Afiliado *</label>
              <input type="text" id="titulo" name="titulo" required value={affiliateData.titulo || ''} onChange={handleChange} />
            </div>
            <div className={s.formGroup}>
              <label htmlFor="bio">Biografia *</label>
              <textarea id="bio" name="bio" rows={4} required value={affiliateData.bio || ''} onChange={handleChange} />
            </div>
            <div className={s.formGroup}>
              <label htmlFor="imagem_perfil">Imagem de Perfil (quadrada, até 200kb)</label>
              <ProfileCrop onCropComplete={setProfileBase64} initialImage={affiliateData.imagem_perfil_url || ''} />
              {affiliateData.imagem_perfil_url && !profileBase64 && (
                <div style={{ marginTop: 8 }}>
                  <img src={affiliateData.imagem_perfil_url} alt="Perfil atual" style={{ maxWidth: 120, borderRadius: 8 }} />
                </div>
              )}
            </div>
            <div className={s.formGroup}>
              <label htmlFor="imagem_banner">Imagem de Banner (1130x320, até 200kb)</label>
              <BannerCrop onCropComplete={setBannerBase64} initialImage={affiliateData.imagem_banner_url || ''} />
              {affiliateData.imagem_banner_url && !bannerBase64 && (
                <div style={{ marginTop: 8 }}>
                  <img src={affiliateData.imagem_banner_url} alt="Banner atual" style={{ maxWidth: 300, borderRadius: 8 }} />
                </div>
              )}
            </div>
          </div>

          <div className={s.formActions}>
            <Button type="submit" loading={loading} disabled={!!slugError || loading}>
              Salvar alterações
            </Button>
          </div>
        </form>
      </Container>
    </div>
  )
}

export default AffiliateChangeData 